import { useEffect, useState } from "react";
import { OperationalMetricsReport } from "@/components/reports/OperationalMetricsReport";
import { Moon, CalendarCheck, Clock, XCircle } from "lucide-react";
import api from "@/lib/api";
import { KpiCard } from "@/components/shared/KpiCard";
import { PageHeader, SectionCard } from "@/components/shared/PageChrome";
import { LoadingState, ErrorState } from "@/components/shared/States";
import { SeriesBars } from "@/components/shared/SeriesBars";

const PALETTE = ["#16213E", "#3157A4", "#F28C28", "#7FA6E0", "#B9C6E0", "#8A94A6"];

export default function ClientAnalytics() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get("/client/analytics").then((r) => setData(r.data)).catch(() => setError(true));
  }, []);

  if (error) return <ErrorState testId="client-analytics-error" />;
  if (!data) return <LoadingState testId="client-analytics-loading" />;

  const byLoc = data.bookings_by_location || [];
  const locTotal = byLoc.reduce((n, l) => n + l.bookings, 0);

  return (
    <div data-testid="client-analytics-page" className="space-y-5">
      <PageHeader title="Analytics & Insights" subtitle="Understand your accommodation programme with detailed insights. All figures are computed from your organisation's recorded bookings." />

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard testId="kpi-accommodation-nights" label="Accommodation Nights" value={data.accommodation_nights} icon={Moon} />
        <KpiCard testId="kpi-total-bookings" label="Total Bookings" value={data.total_bookings} icon={CalendarCheck} />
        <KpiCard testId="kpi-average-stay" label="Average Length of Stay" value={data.average_stay_nights == null ? "—" : `${data.average_stay_nights} nights`} icon={Clock} />
        <KpiCard testId="kpi-cancellations" label="Cancellations" value={data.cancellations} icon={XCircle} tone={data.cancellations ? "warning" : "default"} />
      </div>

      <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <SeriesBars testId="monthly-nights-panel" title="Accommodation nights by month" subtitle="Room-nights in each of the last 12 months (cancelled stays excluded)" tone="blue"
          points={data.monthly_nights || []} valueKey="nights" labelFor={(p) => new Date(`${p.month}-01`).toLocaleDateString("en-GB", { month: "short", year: "2-digit" })}
          emptyText="No accommodation nights recorded in the last 12 months." />

        <SectionCard testId="bookings-by-location" title="Bookings by location" subtitle="Distinct booking-property placements, cancelled stays excluded">
          {locTotal === 0 ? <p data-testid="bookings-by-location-empty" className="text-sm text-jigsaw-muted">No placed bookings yet.</p> : (
            <div className="flex items-center gap-5">
              <Donut segments={byLoc.map((l, i) => ({ value: l.bookings, colour: PALETTE[i % PALETTE.length] }))} total={locTotal} />
              <ul className="min-w-0 flex-1 space-y-2">
                {byLoc.map((l, i) => (
                  <li key={l.location} data-testid={`location-row-${i}`} className="flex items-center justify-between gap-2 text-[13px]">
                    <span className="flex min-w-0 items-center gap-2"><span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: PALETTE[i % PALETTE.length] }} /><span className="truncate text-jigsaw-navy">{l.location}</span></span>
                    <span className="shrink-0 font-medium text-jigsaw-navy">{Math.round((l.bookings / locTotal) * 100)}%<span className="ml-1 text-jigsaw-muted">({l.bookings})</span></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </SectionCard>
      </div>
      <SectionCard title="Operational evidence (your organisation)" subtitle="Staff-recorded actual facts for your placements only. 'Not available' means not yet recorded - never an estimate." testId="client-operational-evidence" bodyClassName="px-5 pb-4">
        <OperationalMetricsReport clientMode />
      </SectionCard>
    </div>
  );
}

function Donut({ segments, total }) {
  const r = 40, c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg viewBox="0 0 100 100" className="h-28 w-28 shrink-0" role="img" aria-label="Bookings by location">
      <circle cx="50" cy="50" r={r} fill="none" stroke="#E6EAF2" strokeWidth="14" />
      {segments.map((s, i) => {
        const len = (s.value / total) * c;
        const el = <circle key={i} cx="50" cy="50" r={r} fill="none" stroke={s.colour} strokeWidth="14" strokeDasharray={`${len} ${c - len}`} strokeDashoffset={-offset} transform="rotate(-90 50 50)" />;
        offset += len;
        return el;
      })}
      <text x="50" y="47" textAnchor="middle" className="fill-jigsaw-navy" style={{ fontSize: 18, fontWeight: 700 }}>{total}</text>
      <text x="50" y="61" textAnchor="middle" style={{ fontSize: 8, fill: "#6B7280" }}>bookings</text>
    </svg>
  );
}
